import { CheckboxGroup, Heading, Text, VStack } from "@chakra-ui/react"
import { isBlocksStep } from "@pathwright/blocks-engine/src/utils/utils"
import { AugmentationPanelCheckbox } from "@pathwright/ux/src/components/augmentation/AugmentationPanelCheckbox"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import SimpleFormikDiscardFormState from "@pathwright/ux/src/components/form/state/SimpleFormikDiscardFormState"
import { Form, Formik, type FormikHelpers } from "formik"
import { useMemo, useState } from "react"
import {
  CoverPagePathItemFragment as CoverPagePreviewPathItem,
  PathItemType,
  useUpdateCoverPagePathPreviewItemsMutation
} from "@pathwright/graphql-client/src/generated"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import { useCoverPagePreviewItems } from "../path-preview/utils"
import { useCoverPageTranslate } from "../utils"
import { CoverPagePanelFormActions } from "./CoverPagePanelFormActions"
import { useCoverPagePanelErrorMessage } from "./utils"

type CoverPagePanelPreviewValues = {
  selectedStepIds: string[]
}

function buildInitialValues(
  previewSteps: CoverPagePreviewPathItem[]
): CoverPagePanelPreviewValues {
  return {
    selectedStepIds: previewSteps
      .filter((step) => step.is_previewable)
      .map((step) => String(step.source_id))
  }
}

function PreviewStepRow({
  step,
  disabled
}: {
  step: CoverPagePreviewPathItem
  disabled: boolean
}) {
  const { t } = useCoverPageTranslate()

  return (
    <AugmentationPanelCheckbox
      value={String(step.source_id)}
      disabled={disabled}
    >
      {step.name ||
        t("path_preview.step_with_number", {
          defaultValue: "Step {{number}}",
          number: step.source_id
        })}
    </AugmentationPanelCheckbox>
  )
}

export function CoverPagePanelPreviewRoute() {
  const { t } = useCoverPageTranslate()
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const resource = useCoverPageContext((value) => value.resource)
  const refetchCoverPageResource = useCoverPageContext(
    (value) => value.refetchCoverPageResource
  )
  const isEditable = useCoverPageIsEditable()
  const { pathItems } = useCoverPagePreviewItems()
  const [updatePathPreviewItems] = useUpdateCoverPagePathPreviewItemsMutation()
  const [successfulSubmissionCount, setSuccessfulSubmissionCount] = useState(0)

  const lessonStepGroups = useMemo(
    () =>
      pathItems.reduce(
        (
          acc: Array<{
            lesson: CoverPagePreviewPathItem
            steps: CoverPagePreviewPathItem[]
          }>,
          item
        ) => {
          if (item.type !== PathItemType.Lesson) return acc

          const steps = (item.items || []).filter(
            (step): step is CoverPagePreviewPathItem =>
              step.type === PathItemType.Step && isBlocksStep(step)
          )

          if (steps.length > 0) {
            acc.push({
              lesson: item,
              steps
            })
          }

          return acc
        },
        []
      ),
    [pathItems]
  )

  const previewSteps = useMemo(
    () => lessonStepGroups.flatMap((group) => group.steps),
    [lessonStepGroups]
  )

  const initialValues = useMemo(
    () => buildInitialValues(previewSteps),
    [previewSteps]
  )

  async function handleSubmit(
    values: CoverPagePanelPreviewValues,
    helpers: FormikHelpers<CoverPagePanelPreviewValues>
  ) {
    if (!resource) return
    try {
      if (!isEditable) return

      const pathId = Number(resource.preview_group?.path?.id)
      if (!pathId) {
        throw new Error(
          t("panel_preview.preview_path_unavailable", {
            defaultValue:
              "Unable to save path preview: preview path is unavailable."
          })
        )
      }

      const selectedStepIds = new Set(values.selectedStepIds)

      // Form a list of items whose is_previewable property should be updated.
      const items = previewSteps
        .filter(
          (item) =>
            item.is_previewable !== selectedStepIds.has(String(item.source_id))
        )
        .map((item) => ({
          source_id: item.source_id,
          is_previewable: selectedStepIds.has(String(item.source_id))
        }))

      const result = await updatePathPreviewItems({
        variables: {
          path_id: pathId,
          items
        }
      })

      const failures =
        result.data?.bulkUpdatePathItems?.filter((item) => !item.success) || []

      if (failures.length > 0) {
        throw new Error(
          failures[0]?.error ||
            t("panel_preview.save_preview_steps_error", {
              defaultValue: "Unable to save one or more preview steps."
            })
        )
      }

      await refetchCoverPageResource()
      helpers.resetForm({ values })
      setSuccessfulSubmissionCount((count) => count + 1)
    } catch (error) {
      toaster.create({
        title: getErrorMessage(
          error,
          t("panel_errors.save_preview", {
            defaultValue: "Error saving Cover Page's preview:"
          })
        ),
        type: "error",
        duration: 4000,
        closable: true
      })
      throw error
    }
  }

  if (!resource) return <></>

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {(form) => {
        const formId = "cover-page-panel-preview"

        return (
          <Form
            id={formId}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <SimpleFormikDiscardFormState
              formStateKey={["cover-page", "preview", resource.id]}
            />
            <KeyboardFormSubmit />
            <VStack align="stretch" gap="augmentationPanel" w="full" pr={1}>
              <Text fontSize="sm" m={0}>
                {t("panel_preview.check_steps_to_show", {
                  defaultValue: "Check the Steps you'd like to show:"
                })}
              </Text>
              <CheckboxGroup
                value={form.values.selectedStepIds}
                onValueChange={(value) => {
                  form.setFieldValue("selectedStepIds", value)
                }}
              >
                {lessonStepGroups.length > 0 ? (
                  lessonStepGroups.map(({ lesson, steps }) => (
                    <VStack
                      key={Number(lesson.source_id)}
                      align="stretch"
                      gap="5px"
                      w="full"
                    >
                      <Heading
                        as="h3"
                        textStyle="augmentationPanel"
                        fontWeight={600}
                        m={0}
                      >
                        {lesson.name ||
                          t("panel_preview.lesson_with_number", {
                            defaultValue: "Lesson {{number}}",
                            number: lesson.source_id
                          })}
                      </Heading>
                      <VStack align="stretch" gap="5px" w="full">
                        {steps.map((step) => {
                          const stepId = Number(step.source_id)
                          return (
                            <PreviewStepRow
                              key={stepId}
                              step={step}
                              disabled={!isEditable || form.isSubmitting}
                            />
                          )
                        })}
                      </VStack>
                    </VStack>
                  ))
                ) : (
                  <Text fontSize="sm" color="fg.muted" m={0}>
                    {t("panel_preview.no_steps_available", {
                      defaultValue: "No steps are available on this path."
                    })}
                  </Text>
                )}
              </CheckboxGroup>
            </VStack>
            <CoverPagePanelFormActions
              form={{ ...form, successfulSubmissionCount }}
              formId={formId}
              disabled={!isEditable}
              submit={{
                children: t("panel_preview.save_path_preview", {
                  defaultValue: "Save Path Preview"
                })
              }}
              discard={{
                children: t("panel.discard", { defaultValue: "Discard" })
              }}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
